import _, {chain, isEqual, isUndefined} from 'lodash';
import {DateTime} from 'luxon';

import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {AccountsSetupGuideTasksEnum} from '../../../models/api/accountsSetupGuideModel';
import {AsyncExportTypesEnum} from '../../../models/api/analyticsExportModel';
import {CommentReactionErrorTypesEnum} from '../../../models/api/commentReactionModel';
import {FileCacheItem} from '../../../models/api/fileCacheItemModel';
import {
  AccountDetailsSectionsEnum,
  ContactDetailsSectionsEnum,
  ContactDetailsTabsEnum,
  LocalPreferencesFlagsEnum,
  LocalPreferencesModel,
  LocalPreferencesSizesEnum,
} from '../../../models/api/localPreferencesModel';
import {RuleModel} from '../../../models/api/ruleModel';
import {SearchHelpCenterResultModel} from '../../../models/api/searchHelpCenterModel';
import {SortOptionModel} from '../../../models/api/sortOptionModel';
import {TagModel} from '../../../models/api/tagModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {InboxesFiltersEnum, InboxesSearchLocationsEnum} from '../../../models/url/inboxesUrlModel';
import {EmojiDefinition} from '../../../services/emojis/emojis';
import {ThunkAction} from '../../appThunk';
import {
  contactMergeInfoBoxDefaultExpandedState,
  contactMergeInfoBoxExpandedKey,
  getFindIsExpanded,
  getLocalPreferences,
} from './localPreferencesSelectors';

/*
 * Action types.
 */

export enum LocalPreferencesActionsEnum {
  LOCAL_PREFERENCES_UPDATED = '@@data/localPreferences/LOCAL_PREFERENCES_UPDATED',
}

/*
 * Types.
 */

export interface LocalPreferencesUpdatedAction extends ActionWithPayload<LocalPreferencesModel> {
  type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED;
}

/*
 * Action creators.
 */

export type Filter = InboxesFiltersEnum | InboxesSearchLocationsEnum;

export function updateSelectedFilterAction(key: string, filter: Filter): ThunkAction {
  return (dispatch, getState) => {
    // If the last filter is the same, nothing to do.
    const localPreferences = getLocalPreferences(getState());
    const {selectedFilters} = localPreferences;
    if (selectedFilters[key] === filter) {
      return null;
    }

    // Otherwise, update the filter.
    const newSelectedFilters = {
      ...selectedFilters,
      [key]: filter,
    };

    const newPreferences = {
      ...localPreferences,
      selectedFilters: newSelectedFilters,
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };
    return dispatch(action);
  };
}

export function updateSortOptionAction(key: string, sortOption: SortOptionModel): ThunkAction {
  return (dispatch, getState) => {
    // If the last sort option is the same, nothing to do.
    const localPreferences = getLocalPreferences(getState());
    const {sortOptions} = localPreferences;
    if (isEqual(sortOptions[key], sortOption)) {
      return null;
    }

    // Otherwise, update the sort option.
    const newSortOptions = {
      ...sortOptions,
      [key]: sortOption,
    };

    const newPreferences = {
      ...localPreferences,
      sortOptions: newSortOptions,
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };
    return dispatch(action);
  };
}

export function toggleExpandItemAction(key: string, defaultValue: boolean): ThunkAction {
  return (dispatch, getState) => {
    // Find the current value in the state.
    const state = getState();
    const localPreferences = getLocalPreferences(getState());
    const isExpanded = getFindIsExpanded(state)(key, defaultValue);

    // Update it.
    const newCollapsedItems = {
      ...localPreferences.collapsedItems,
      [key]: isExpanded,
    };

    const newPreferences = {
      ...localPreferences,
      collapsedItems: newCollapsedItems,
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };
    return dispatch(action);
  };
}

export function toggleDismissChannelErrorsAction(
  channelIds: ReadonlyArray<number>,
  isDismissed: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {dismissedChannelErrors} = localPreferences;

    const newDismissedChannelErrors = {
      ...dismissedChannelErrors,
      ...chain(channelIds)
        .mapKeys((id) => id)
        .mapValues(() => isDismissed)
        .value(),
    };

    const newPreferences = {
      ...localPreferences,
      dismissedChannelErrors: newDismissedChannelErrors,
    };

    // Update the state
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };

    return dispatch(action);
  };
}

export function dismissIntegrationErrorAction(key: number): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {dismissedIntegrationErrors} = localPreferences;

    // Log the time that the user has dismissed the error.
    const newDismissedIntegrationErrors = {
      ...dismissedIntegrationErrors,
      [key]: Date.now(),
    };

    const newPreferences = {
      ...localPreferences,
      dismissedIntegrationErrors: newDismissedIntegrationErrors,
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };

    return dispatch(action);
  };
}

export function dismissIntegrationInformationBannerAction(integrationType: string): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {dismissedIntegrationInformationBanners} = localPreferences;

    if (dismissedIntegrationInformationBanners.includes(integrationType)) {
      return null;
    }

    const newDismissedIntegrationInformationBanners = [
      ...dismissedIntegrationInformationBanners,
      integrationType,
    ];

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        dismissedIntegrationInformationBanners: newDismissedIntegrationInformationBanners,
      },
    };

    return dispatch(action);
  };
}

export function requestIntegrationAction(integrationType: string): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {requestedIntegrations} = localPreferences;

    if (requestedIntegrations.includes(integrationType)) {
      return null;
    }

    const newRequestedIntegrations = [...requestedIntegrations, integrationType];

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        requestedIntegrations: newRequestedIntegrations,
      },
    };

    return dispatch(action);
  };
}

export function dismissIntegrationRequestAction(integrationType: string): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {requestedIntegrations} = localPreferences;

    const newRequestedIntegrations = requestedIntegrations.filter((i) => i !== integrationType);

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        requestedIntegrations: newRequestedIntegrations,
      },
    };

    return dispatch(action);
  };
}

export function toggleFlagAction(flag: LocalPreferencesFlagsEnum, newValue?: boolean): ThunkAction {
  return (dispatch, getState) => {
    // If the existing value is the same, nothing to do.
    const localPreferences = getLocalPreferences(getState());
    const {flags} = localPreferences;
    if (flags[flag] === newValue) {
      return null;
    }

    // Otherwise, build the new flags.
    const newFlags = {
      ...flags,
      [flag]: isUndefined(newValue) ? !flags[flag] : newValue,
    };

    // Update the state.
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        flags: newFlags,
      },
    };
    return dispatch(action);
  };
}

export type SizeUpdater = (size: LocalPreferencesSizesEnum, newValue: number) => void;

export function updateSizeAction(size: LocalPreferencesSizesEnum, newValue: number): ThunkAction {
  return (dispatch, getState) => {
    // If the existing value is the same, nothing to do.
    const localPreferences = getLocalPreferences(getState());
    const {sizes} = localPreferences;
    if (sizes[size] === newValue) {
      return null;
    }

    // Otherwise, build the new sizes.
    const newSizes = {
      ...sizes,
      [size]: newValue,
    };

    // Update the state.
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        sizes: newSizes,
      },
    };
    return dispatch(action);
  };
}

export function updateSelectedSideFrameIdAction(newSelectedFrameId: string | undefined): ThunkAction {
  return (dispatch, getState) => {
    // If the existing value is the same, nothing to do.
    const localPreferences = getLocalPreferences(getState());
    const {selectedSideFrameId} = localPreferences;
    if (selectedSideFrameId === newSelectedFrameId) {
      return null;
    }

    // Update the state.
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        lastSelectedSideFrameId: newSelectedFrameId || localPreferences.lastSelectedSideFrameId,
        selectedSideFrameId: newSelectedFrameId,
      },
    };
    return dispatch(action);
  };
}

export function recordTeammateInteractionsAction(teammateIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    if (teammateIds.length === 0) {
      return null;
    }

    const localPreferences = getLocalPreferences(getState());
    const {teammateInteractions} = localPreferences;

    const now = Date.now();
    const cutoff = DateTime.local().minus({days: 30}).toMillis();

    // Add the new interactions, and clean the collection.
    const newTeammateInteractions = _(teammateIds)
      .map((id) => ({teammateId: id, date: now}))
      .concat(teammateInteractions)
      .filter((i) => i.date > cutoff) // Cut at 30 days.
      .sortBy((i) => i.date)
      .takeRight(300) // Keep 300 at most.
      .value();

    // Update the state.
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        teammateInteractions: newTeammateInteractions,
      },
    };
    return dispatch(action);
  };
}

export function recordEmojiUseAction(emoji: EmojiDefinition): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {emojiUsage} = localPreferences;

    const cutoff = DateTime.local().minus({days: 30}).toMillis();

    // Add the new emoji use, and clean the collection.
    const newEmojiUsage = _([{shortcode: emoji.shortcode, date: Date.now()}])
      .concat(emojiUsage)
      .filter((i) => i.date > cutoff) // Cut at 30 days.
      .sortBy((i) => i.date)
      .takeRight(100) // Keep 100 at most.
      .value();

    // Update the state.
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        emojiUsage: newEmojiUsage,
      },
    };
    return dispatch(action);
  };
}

export function recordUnassignedAssignedSwitchAction(): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        unassignedAssignedSwitchCount: localPreferences.unassignedAssignedSwitchCount + 1,
      },
    };

    return dispatch(action);
  };
}

export function toggleRuleFromDragOptionsAction(
  options: {
    tag: TagModel;
    recipients: ReadonlyArray<VisualRecipientModel>;
  },
  hideDialog?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        ruleFromDragOptions: hideDialog ? undefined : options,
      },
    };

    return dispatch(action);
  };
}

export function toggleLastRuleCreatedAction(rule: RuleModel, hideBanner?: boolean): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        lastRuleCreated: hideBanner ? undefined : rule,
      },
    };

    return dispatch(action);
  };
}

export function toggleLastSuppressionListsRemovalAction(email: string, hideBanner?: boolean): ThunkAction {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        lastSuppressionListsRemoval: hideBanner ? undefined : email,
      },
    };

    return dispatch(action);
  };
}

export function addCommentReactionErrorAction(commentErrorType: CommentReactionErrorTypesEnum): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {commentReactionErrors} = localPreferences;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        commentReactionErrors: commentReactionErrors.concat([{type: commentErrorType, id: randomString()}]),
      },
    };

    return dispatch(action);
  };
}

export function removeCommentReactionErrorAction(errorId: string): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {commentReactionErrors} = localPreferences;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        commentReactionErrors: commentReactionErrors.filter((error) => error.id !== errorId),
      },
    };

    return dispatch(action);
  };
}

export function updateHelpCenterSearchCacheAction(
  query?: string,
  result?: SearchHelpCenterResultModel,
): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {helpCenterSearchCache} = localPreferences;

    if (helpCenterSearchCache?.query === query) {
      return null;
    }

    const updatedQuery = query && result ? {query, result} : undefined;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        helpCenterSearchCache: updatedQuery,
      },
    };

    return dispatch(action);
  };
}

export function updateFileCacheItemsAction(newFileCacheItems: ReadonlyArray<FileCacheItem>): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());
    const {fileCacheItems} = localPreferences;

    if (fileCacheItems === newFileCacheItems) {
      return;
    }

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        fileCacheItems: newFileCacheItems,
      },
    };

    dispatch(action);
  };
}

export function updateContactDetailsSectionExpandedAction(
  section: ContactDetailsSectionsEnum,
  state: boolean,
): ThunkAction<void> {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        contactDetailsPreferences: {
          ...localPreferences.contactDetailsPreferences,
          sectionsExpandState: {
            ...localPreferences.contactDetailsPreferences.sectionsExpandState,
            [section]: state,
          },
        },
      },
    };
    dispatch(action);
  };
}

export function updateContactDetailsPreferredTabAction(
  preferredTab: ContactDetailsTabsEnum | undefined,
): ThunkAction<void> {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        contactDetailsPreferences: {
          ...localPreferences.contactDetailsPreferences,
          preferredTab,
        },
      },
    };
    dispatch(action);
  };
}

export function updateAccountDetailsSectionExpandedAction(
  section: AccountDetailsSectionsEnum,
  state: boolean,
): ThunkAction<void> {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        accountDetailsPreferences: {
          ...localPreferences.accountDetailsPreferences,
          sectionsExpandState: {
            ...localPreferences.accountDetailsPreferences.sectionsExpandState,
            [section]: state,
          },
        },
      },
    };
    dispatch(action);
  };
}

export function toggleShouldDisplayImportCSVInfoBannerAction(): ThunkAction<void> {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const updatedState = !localPreferences.accountDetailsPreferences?.shouldDisplayImportCSVInfoBanner;
    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        accountDetailsPreferences: {
          ...(localPreferences.accountDetailsPreferences ?? {}),
          shouldDisplayImportCSVInfoBanner: updatedState,
        },
      },
    };
    dispatch(action);
  };
}

export function dismissSettingsInfoBoxAction(infoBoxId: string): ThunkAction {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;
    const {dismissedSettingsInfoBoxes} = localPreferences;

    const newDismissedSettingsInfoBoxes = {
      ...dismissedSettingsInfoBoxes,
      [infoBoxId]: true,
    };

    const newPreferences = {
      ...localPreferences,
      dismissedSettingsInfoBoxes: newDismissedSettingsInfoBoxes,
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };

    return dispatch(action);
  };
}

/**
 * Update the pre-selected columns for the analytics exporter.
 */
export function updateAnalyticsExportsColumnsAction(
  exportType: AsyncExportTypesEnum,
  columns: ReadonlyArray<string>,
): ThunkAction {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const messagesColumns =
      exportType === AsyncExportTypesEnum.MESSAGES
        ? columns
        : localPreferences.analyticsExportsColumns?.messagesColumns;
    const activitiesColumns =
      exportType === AsyncExportTypesEnum.ACTIVITIES
        ? columns
        : localPreferences.analyticsExportsColumns?.activitiesColumns;
    const csatColumns =
      exportType === AsyncExportTypesEnum.CSAT
        ? columns
        : localPreferences.analyticsExportsColumns?.csatColumns;

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        analyticsExportsColumns: {
          messagesColumns,
          activitiesColumns,
          csatColumns,
        },
      },
    };
    dispatch(action);
  };
}

export function toggleContactMergeInfoBoxAction(): ThunkAction {
  return (dispatch) => {
    dispatch(toggleExpandItemAction(contactMergeInfoBoxExpandedKey, contactMergeInfoBoxDefaultExpandedState));
  };
}

// TODO: Remove the toggle when done
export function dismissDefaultConferencingBannerAction(toggle?: boolean): ThunkAction {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const newPreferences: LocalPreferencesModel = {
      ...localPreferences,
      dismissedDefaultConferencingBanner: {
        hasInteracted: isUndefined(toggle) ? true : toggle,
      },
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };

    return dispatch(action);
  };
}

export function updateSidepanelMultiViewSelectedFrameIdsAction(
  selectedFrameIds: ReadonlyArray<string | null>,
): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        sidepanelMultiViewDetails: {
          ...localPreferences.sidepanelMultiViewDetails,
          selectedFrameIds,
        },
      },
    };

    return dispatch(action);
  };
}

export function updateSidepanelMultiViewFrameHeightsAction(frameHeights: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        sidepanelMultiViewDetails: {
          ...localPreferences.sidepanelMultiViewDetails,
          frameHeights,
        },
      },
    };

    return dispatch(action);
  };
}

export function updateAccountsSetupGuideCompletedTasksAction(
  completedTasks: ReadonlyArray<AccountsSetupGuideTasksEnum>,
): ThunkAction {
  return (dispatch, getState) => {
    const {localPreferences} = getState().data;

    const newPreferences = {
      ...localPreferences,
      accountsSetupGuideCompletedTasks: completedTasks,
    };

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: newPreferences,
    };

    return dispatch(action);
  };
}

export function updateEnabledIntegrationIdsAction(enabledIntegrationIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        enabledIntegrationIds,
      },
    };

    return dispatch(action);
  };
}

export function updateEnabledLegacyApplicationIdsAction(
  enabledLegacyApplicationIds: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const localPreferences = getLocalPreferences(getState());

    const action: LocalPreferencesUpdatedAction = {
      type: LocalPreferencesActionsEnum.LOCAL_PREFERENCES_UPDATED,
      payload: {
        ...localPreferences,
        enabledLegacyApplicationIds,
      },
    };

    return dispatch(action);
  };
}

/*
 * Union type.
 */

export type LocalPreferencesAction = LocalPreferencesUpdatedAction;
